import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "API"
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "api"
    }}>{`API`}</h1>
    <h2 {...{
      "id": "theme-ui"
    }}><inlineCode parentName="h2">{`theme-ui`}</inlineCode></h2>
    <p>{`The core `}<inlineCode parentName="p">{`theme-ui`}</inlineCode>{` package exports the following modules.`}</p>
    <h3 {...{
      "id": "jsx-runtime"
    }}><inlineCode parentName="h3">{`/jsx-runtime`}</inlineCode></h3>
    <p>{`While not being available in the `}<inlineCode parentName="p">{`theme-ui`}</inlineCode>{` package directly, there are two separate entry points for React create element functions intended to be used in the `}<a parentName="p" {...{
        "href": "https://reactjs.org/blog/2020/09/22/introducing-the-new-jsx-transform.html"
      }}>{`automatic JSX runtime`}</a>{` with a custom pragma comment.
They add support for the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop, which uses Emotion's create element functions internally and parse style objects with the Theme UI `}<inlineCode parentName="p">{`css`}</inlineCode>{` utility.`}</p>
    <p><strong parentName="p">{`Usage:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsxImportSource theme-ui */

export default (props) => (
  <div
    {...props}
    sx={{
      color: 'primary',
    }}
  />
)
`}</code></pre>
    <p><strong parentName="p">{`Imports for use without JSX:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { jsx, jsxs } from 'theme-ui/jsx-runtime'
import { jsxDEV } from 'theme-ui/jsx-dev-runtime'
`}</code></pre>
    <h3 {...{
      "id": "jsx"
    }}><inlineCode parentName="h3">{`jsx`}</inlineCode></h3>
    <p>{`The `}<inlineCode parentName="p">{`jsx`}</inlineCode>{` export is a React create element function for the classic JSX runtime intended for use with a custom pragma comment.
It adds support for the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop in the same way like the `}<inlineCode parentName="p">{`/jsx-runtime`}</inlineCode>{` entrypoint.`}</p>
    <p><strong parentName="p">{`Usage:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsx jsx */
import { jsx } from 'theme-ui'

export default (props) => (
  <div
    {...props}
    sx={{
      color: 'primary',
    }}
  />
)
`}</code></pre>
    <h3 {...{
      "id": "themeprovider"
    }}><inlineCode parentName="h3">{`ThemeProvider`}</inlineCode></h3>
    <p>{`The `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` component is a wrapper around Emotion's `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` and MDX's `}<inlineCode parentName="p">{`MDXProvider`}</inlineCode>{`.
It provides default styled components to MDX content that pick up values from `}<inlineCode parentName="p">{`theme.styles`}</inlineCode>{`.
It also handles color mode state.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`theme`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Theming context object`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`components`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional custom MDX components`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`children`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Node`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`React children`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "usethemeui"
    }}><inlineCode parentName="h3">{`useThemeUI`}</inlineCode></h3>
    <p>{`The `}<inlineCode parentName="p">{`useThemeUI`}</inlineCode>{` hook returns the full Theme UI context object, which includes `}<inlineCode parentName="p">{`theme`}</inlineCode>{`, and color mode.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const context = useThemeUI()
`}</code></pre>
    <h3 {...{
      "id": "context"
    }}><inlineCode parentName="h3">{`Context`}</inlineCode></h3>
    <p>{`The React context used in Theme UI.`}</p>
    <h3 {...{
      "id": "usecolormode"
    }}><inlineCode parentName="h3">{`useColorMode`}</inlineCode></h3>
    <p>{`The `}<inlineCode parentName="p">{`useColorMode`}</inlineCode>{` hook returns an array with two values: the current color mode state and an updater function.
The color mode state can be any string and should match the name of a color mode defined in `}<inlineCode parentName="p">{`theme.colors.modes`}</inlineCode>{` or be a name for the default color mode.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const [colorMode, setColorMode] = useColorMode()
`}</code></pre>
    <h3 {...{
      "id": "themed"
    }}><inlineCode parentName="h3">{`Themed`}</inlineCode></h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/themed"
      }}><inlineCode parentName="a">{`Themed`}</inlineCode></a>{` export is an object of Emotion styled components that map to keys in the `}<inlineCode parentName="p">{`theme.styles`}</inlineCode>{` object.
Each component will pick up styles from its corresponding theme object.
These are primarily meant to be used as a mechanism to use styles defined in a `}<inlineCode parentName="p">{`theme`}</inlineCode>{` object outside of MDX.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// picks up styles from \`theme.styles.h1\`
<Themed.h1 />
`}</code></pre>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`as`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String or Component`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Changes the underlying rendered element`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "basestyles"
    }}><inlineCode parentName="h3">{`BaseStyles`}</inlineCode></h3>
    <p>{`The `}<inlineCode parentName="p">{`BaseStyles`}</inlineCode>{` component can be used to style child content that isn't generated from MDX.`}</p>
    <Note mdxType="Note">
      <p>{`Note that this will increase the CSS specificity of child elements, and you may run into issues with the cascade (i.e. specificity) when using this component.`}</p>
    </Note>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<BaseStyles>
  <h1>
    Styled based on <code>theme.styles</code>
  </h1>
</BaseStyles>
`}</code></pre>
    <h3 {...{
      "id": "css"
    }}><inlineCode parentName="h3">{`css`}</inlineCode></h3>
    <p>{`As an alternative to using the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop, the `}<inlineCode parentName="p">{`css`}</inlineCode>{` utility is intended for use with the Emotion `}<inlineCode parentName="p">{`css`}</inlineCode>{` prop.
It will attempt to use values from the `}<inlineCode parentName="p">{`theme`}</inlineCode>{` object for many common CSS properties, and will fall back to raw values when there is no corresponding theme value.`}</p>
    <p>{`To see a list of supported CSS properties, see the `}<a parentName="p" {...{
        "href": "/sx-prop"
      }}><inlineCode parentName="a">{`sx`}</inlineCode>{` prop docs`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`css(styleObject)(theme)
`}</code></pre>
    <p><strong parentName="p">{`Usage:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<div css={css(styles)} />
`}</code></pre>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Argument`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`styleObject`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`a theme-aware style object with support for responsive array values and Styled System prop shortcuts`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`theme`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`the Emotion theming context object`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "get"
    }}><inlineCode parentName="h3">{`get`}</inlineCode></h3>
    <p>{`An existential getter function used internally.`}</p>
    <h3 {...{
      "id": "merge"
    }}><inlineCode parentName="h3">{`merge`}</inlineCode></h3>
    <p>{`A function to deeply merge themes.`}</p>
    <p><strong parentName="p">{`Usage`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import dark from '@theme-ui/preset-dark'

const theme = merge(dark, {
  fontWeights: {
    body: 500,
    heading: 700,
    bold: 900,
  },
})
`}</code></pre>
    <h3 {...{
      "id": "initializecolormode"
    }}><inlineCode parentName="h3">{`InitializeColorMode`}</inlineCode></h3>
    <p>{`Used internally by `}<a parentName="p" {...{
        "href": "/packages/gatsby-plugin"
      }}><inlineCode parentName="a">{`gatsby-plugin-theme-ui`}</inlineCode></a>{`, this component can be rendered in HTML before the main application to help prevent the flash of colors that can occur during rehydration.`}</p>
    <p><strong parentName="p">{`Usage`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<InitializeColorMode />
`}</code></pre>
    <h3 {...{
      "id": "components"
    }}><inlineCode parentName="h3">{`components`}</inlineCode></h3>
    <p>{`An object of styled MDX components for use with `}<inlineCode parentName="p">{`MDXProvider`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "components-1"
    }}>{`Components`}</h2>
    <p>{`For component API docs, see `}<a parentName="p" {...{
        "href": "/components"
      }}>{`Components`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      